// SEO landing page for an individual scheduled-task template.
//
// In-app the template catalog (/tasks/browse) opens templates as modals
// (SetupTaskFromTemplateModal). That works great for authenticated users
// but gives Google nothing to crawl. This route fills that gap: every
// enabled template is statically pre-rendered as /tasks/template/<slug>
// with its own title, description, JSON-LD, and a CTA that funnels users
// back into the existing modal flow.
//
// Header (sidebar + back + schedule CTA): `getTaskTemplateDetailRouteNavigationOptions`
// in `TaskTemplateDetailRouteNavigationOptions.jsx`.
//
// Layout aligns with deck recipe detail + tool long-form copy: hero,
// gradient “how it works” frame, ToolPageCopy, bottom CTA, related grid.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import TemplatePinCard from '@/components/Tasks/TemplatePinCard';
import { useTaskTemplatesStore, useModalStore } from '@/store';
import { taskTemplateApi as taskTemplateApiSsr } from '@/lib/taskTemplateApi';
import useRequireAuth from '@/hooks/useRequireAuth';
import { BORDER_RADIUS } from '@/constants/config';
import {
  colorForSlug,
  heroChromaForSlug,
  isNewTemplate,
  scheduleLabel,
} from '@/utils/tasks_catalog';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
const _staticTemplateCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------
const SCHEDULE_LABEL = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  annually: 'Yearly',
};
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatTimeOfDay = (hour, minute) => {
  if (hour === undefined || hour === null) return '';
  const h12 = ((hour + 11) % 12) + 1;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const mm = String(minute || 0).padStart(2, '0');
  return `${h12}:${mm} ${ampm}`;
};

const summarizeSchedule = (sched) => {
  if (!sched?.type) return '';
  const time = formatTimeOfDay(sched?.timeOfDay?.hour, sched?.timeOfDay?.minute);
  const base = SCHEDULE_LABEL[sched.type] || sched.type;
  if (sched.type === 'weekly' && Number.isFinite(sched.daysOfWeek)) {
    return `${base} on ${DAYS_OF_WEEK[sched.daysOfWeek]} at ${time}`;
  }
  if (sched.type === 'monthly' && sched.dayOfMonth) {
    return `${base} on day ${sched.dayOfMonth} at ${time}`;
  }
  if (sched.type === 'once') return 'Run once';
  return `${base} at ${time}`;
};

const FIELD_TYPE_LABEL = {
  text: 'Text',
  textarea: 'Long text',
  select: 'Single choice',
  multiselect: 'Multiple choice',
  number: 'Number',
  boolean: 'Yes/no toggle',
};

const FIELD_TYPE_ICON = {
  text: 'font',
  textarea: 'align-left',
  select: 'list',
  multiselect: 'list-check',
  number: 'hash',
  boolean: 'toggle-on',
};

/** Normalize admin-authored `meta.pageCopy` into ToolPageCopy blocks. */
function normalizeTaskTemplatePageCopy(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((b) => {
        if (!b || typeof b !== 'object') return null;
        const type = String(b.type || '').trim();
        if (!type) return null;
        return { ...b, type };
      })
      .filter(Boolean);
  }
  if (typeof raw === 'object') {
    const blocks = [];
    if (raw.hero && typeof raw.hero === 'object') {
      blocks.push({ type: 'hero', ...raw.hero });
    }
    if (Array.isArray(raw.sections)) {
      raw.sections.forEach((s) => {
        if (s && typeof s === 'object') blocks.push(s);
      });
    }
    return blocks;
  }
  return [];
}

function buildFaqBlock(faq) {
  if (!Array.isArray(faq) || !faq.length) return null;
  const items = faq
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const q = String(row.q || row.question || '').trim();
      const a = String(row.a || row.answer || '').trim();
      if (!q || !a) return null;
      return { q, a };
    })
    .filter(Boolean);
  if (!items.length) return null;
  return { type: 'faq', title: 'FAQ', items };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const createStyles = (
  colors,
  isXS,
  isSM,
  isXL,
  insetBottom,
  accent,
  heroChroma,
) => {
  const stacked = isXS || isSM;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1, width: '100%' },
    scrollContent: { width: '100%', paddingBottom: insetBottom + 100 },
    fullBleedBand: {
      width: '100%',
      alignSelf: 'stretch',
      paddingHorizontal: 0,
      paddingTop: 16,
      marginBottom: 18,
    },
    inner: {
      width: '100%',
      maxWidth: isXL ? 1100 : 960,
      alignSelf: 'center',
      paddingHorizontal: isXS ? 16 : 28,
      paddingTop: 0,
      gap: 28,
    },

    // "What is a task template?" + "How it runs" — side-by-side on
    // desktop, stacked on small screens. Both cards stretch to equal
    // height via `alignItems: 'stretch'` + `flex: 1` on each child.
    featureRow: {
      flexDirection: stacked ? 'column' : 'row',
      gap: stacked ? 14 : 18,
      alignItems: 'stretch',
    },

    explainerCard: {
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 18 : 22,
      gap: 12,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      position: 'relative',
    },
    explainerHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    explainerIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
      borderWidth: 1,
      borderColor: `${accent}33`,
    },
    explainerEyebrow: {
      textTransform: 'uppercase',
      letterSpacing: 1.4,
      fontSize: 11,
      fontWeight: '800',
      marginBottom: 2,
    },
    explainerFeatures: {
      gap: 10,
      marginTop: 6,
    },
    explainerFeatureRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
    },
    explainerFeatureIcon: {
      width: 28,
      height: 28,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1a`,
      marginTop: 1,
    },

    previewFrame: {
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_block,
      flex: 1,
      minWidth: 0,
    },
    previewAccentBar: { height: 4, width: '100%' },
    previewInner: {
      padding: isXS ? 18 : 22,
      gap: 14,
    },
    previewHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    previewIconWrap: {
      width: 36,
      height: 36,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
      borderWidth: 1,
      borderColor: `${accent}33`,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 1.3,
      textTransform: 'uppercase',
      opacity: 0.6,
    },

    // Schedule callout — accent-tinted pill row at the top of the
    // capabilities frame so the cadence reads at a glance.
    scheduleBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: `${accent}33`,
      backgroundColor: `${accent}10`,
    },
    scheduleIconWrap: {
      width: 28,
      height: 28,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
    },

    // Compact "what you configure" field rows. Same slim affordance as
    // the agents page starter prompts: borderless row → tinted on hover.
    stepsWrap: { gap: 8 },
    stepCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 9,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
    },
    stepIcon: {
      width: 26,
      height: 26,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
    },
    stepMeta: {
      fontSize: 10,
      fontWeight: '800',
      opacity: 0.7,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },

    copySection: {
      marginTop: 8,
      gap: 8,
    },

    // "Ready to automate?" — centered hero-language CTA on a soft
    // accent wash. Matches `CatalogDetailHero` chroma so the tint reads
    // identical to the page header and sits on `solid_color_block`.
    bottomCta: {
      marginTop: 8,
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
      position: 'relative',
    },
    bottomCtaWashSoft: { ...StyleSheet.absoluteFillObject },
    bottomCtaBlobA: {
      position: 'absolute',
      width: isXS ? 280 : 360,
      height: isXS ? 280 : 360,
      borderRadius: 400,
      top: isXS ? -100 : -118,
      right: isXS ? -72 : -88,
      backgroundColor: heroChroma.blobA,
      opacity: Platform.OS === 'web' ? 0.52 : 0.5,
      ...(Platform.OS === 'web' ? { filter: 'blur(44px)' } : null),
    },
    bottomCtaBlobB: {
      position: 'absolute',
      width: isXS ? 200 : 260,
      height: isXS ? 200 : 260,
      borderRadius: 300,
      bottom: isXS ? -110 : -130,
      left: isXS ? -70 : -90,
      backgroundColor: heroChroma.blobB,
      opacity: Platform.OS === 'web' ? 0.38 : 0.36,
      ...(Platform.OS === 'web' ? { filter: 'blur(36px)' } : null),
    },
    bottomCtaInner: {
      padding: isXS ? 28 : 44,
      gap: 12,
      alignItems: 'center',
    },
    bottomCtaBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 5,
      paddingHorizontal: 11,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: `${accent}55`,
      backgroundColor: `${accent}1a`,
      marginBottom: 6,
    },
    bottomCtaTitle: {
      fontSize: isXS ? 28 : 38,
      lineHeight: isXS ? 32 : 44,
      letterSpacing: -0.6,
      fontWeight: '800',
      textAlign: 'center',
      marginBottom: 0,
    },
    bottomCtaBody: {
      fontSize: isXS ? 15 : 16,
      lineHeight: isXS ? 22 : 24,
      textAlign: 'center',
      maxWidth: 540,
      opacity: 0.78,
    },
    bottomCtaButton: {
      marginTop: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 14,
      paddingHorizontal: 26,
      borderRadius: 999,
      backgroundColor: accent,
      ...(Platform.OS === 'web'
        ? {
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            transition: 'box-shadow 0.2s ease, background-color 0.15s ease',
          }
        : null),
    },

    relatedSection: {
      marginTop: 8,
      gap: 14,
    },
    relatedHeader: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
    },
    // Grid stretches every cell to row height so a 1-line vs 3-line
    // description doesn't produce wildly different card heights.
    relatedCardGrid: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      alignItems: stacked ? 'flex-start' : 'stretch',
      gap: isXS ? 10 : isSM ? 10 : 12,
    },
    relatedCell: {
      minWidth: 0,
      flexShrink: 0,
      alignSelf: stacked ? 'flex-start' : 'stretch',
      ...(Platform.OS === 'web' ? { display: 'flex', flexDirection: 'column' } : {}),
    },

    notFound: {
      paddingVertical: 48,
      alignItems: 'center',
      gap: 16,
    },
    loadingBox: {
      paddingVertical: 80,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
    },
  });
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function TaskTemplateDetailPage() {
  const params = useLocalSearchParams();
  // expo-router can pass `slug` as either a string or an array (catch-all
  // segments, repeated query params). Normalize once so every downstream
  // lookup (`_staticTemplateCache[slug]`, canonical URLs, find-by-slug, etc.)
  // receives a plain string and never an `[object Object]` template URL.
  const slug = useMemo(() => {
    const raw = params?.slug;
    if (Array.isArray(raw)) return raw[0] || null;
    return typeof raw === 'string' && raw ? raw : null;
  }, [params?.slug]);
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isXL, isSM, isMD, showsScrollIndicator } = useResponsiveness();
  const { width: windowWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [relatedGridWidth, setRelatedGridWidth] = useState(null);

  const templates = useTaskTemplatesStore((s) => s.templates);
  const categories = useTaskTemplatesStore((s) => s.categories);
  const getTemplatesByCategory = useTaskTemplatesStore((s) => s.getTemplatesByCategory);
  const fetchTemplates = useTaskTemplatesStore((s) => s.fetchTemplates);
  const isFetched = useTaskTemplatesStore((s) => s.isFetched);
  const isLoading = useTaskTemplatesStore((s) => s.isLoading);
  const { openModal } = useModalStore((s) => s.actions);
  const requireAuth = useRequireAuth();

  useEffect(() => {
    if (!isFetched && !isLoading) fetchTemplates();
  }, [isFetched, isLoading, fetchTemplates]);

  const staticTemplate = slug ? _staticTemplateCache[slug] : null;
  const liveTemplate = useMemo(
    () => templates.find((tpl) => tpl.slug === slug),
    [templates, slug],
  );
  const template = liveTemplate || staticTemplate;

  const categorySlug = template?.category || '';
  const accent = useMemo(() => colorForSlug(categorySlug), [categorySlug]);
  const heroChroma = useMemo(() => heroChromaForSlug(categorySlug), [categorySlug]);
  const categoryLabel = useMemo(() => {
    const c = categories.find((x) => x.slug === categorySlug);
    return c?.name || categorySlug || 'Template';
  }, [categories, categorySlug]);

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isXL, insets.bottom, accent, heroChroma),
    [colors, isXS, isSM, isXL, insets.bottom, accent, heroChroma],
  );

  const pinCols = isXS ? 1 : isSM ? 1 : isMD ? 2 : isXL ? 3 : 2;
  const gridGapPx = isXS ? 10 : isSM ? 10 : 12;

  // Percent + calc() widths are unreliable inside ScrollView + flexWrap on RN Web;
  // derive column width from measured row (fallback: same math as `inner` max width).
  const innerMaxWidth = isXL ? 1100 : 960;
  const innerPadX = (isXS ? 16 : 28) * 2;
  const fallbackRelatedGridWidth = Math.max(
    0,
    Math.min(windowWidth, innerMaxWidth) - innerPadX,
  );
  const effectiveRelatedGridWidth = relatedGridWidth ?? fallbackRelatedGridWidth;
  const pinWidthPx = useMemo(() => {
    const cols = Math.max(1, pinCols);
    const w = effectiveRelatedGridWidth;
    if (!(w > 0)) return 0;
    const gapTotal = gridGapPx * Math.max(0, cols - 1);
    return Math.max(0, Math.floor((w - gapTotal) / cols));
  }, [effectiveRelatedGridWidth, pinCols, gridGapPx]);

  const onRelatedGridLayout = useCallback((e) => {
    const nw = Math.floor(e.nativeEvent.layout.width);
    if (nw > 0) setRelatedGridWidth((prev) => (prev === nw ? prev : nw));
  }, []);

  const handleTemplateOpenDetail = useCallback(
    (tpl) => {
      const s = tpl?.slug;
      if (!s) return;
      router.push(`/tasks/template/${encodeURIComponent(s)}`);
    },
    [router],
  );

  const handleTemplateScheduleFromCatalog = useCallback(
    (tpl) => {
      requireAuth(
        () => openModal('SetupTaskFromTemplateModal', { template: tpl }),
        {
          source: 'tasks_template_detail_related_setup',
          title: `Sign in to set up ${tpl?.name || 'this task'}`,
          subtitle: 'Scheduled tasks run on your account so we know when to start them and where to send the output.',
          icon: 'calendar-clock',
        }
      );
    },
    [openModal, requireAuth],
  );

  const pageCopyBlocks = useMemo(() => {
    if (!template) return [];
    const raw = template.meta?.pageCopy;
    const normalized = normalizeTaskTemplatePageCopy(raw);
    const faqBlock = buildFaqBlock(template.meta?.faq);
    return faqBlock ? [...normalized, faqBlock] : normalized;
  }, [template]);

  const pageCopyPalette = useMemo(
    () => ({
      bg: `${accent}22`,
      border: `${accent}55`,
      text: colors.text_color_primary,
    }),
    [accent, colors.text_color_primary],
  );

  const relatedTemplates = useMemo(() => {
    if (!template?.slug || !categorySlug) return [];
    return getTemplatesByCategory(categorySlug)
      .filter((t) => t.slug && t.slug !== template.slug)
      .sort((a, b) => {
        const ua = a.stats?.usageCount || 0;
        const ub = b.stats?.usageCount || 0;
        if (ub !== ua) return ub - ua;
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      })
      .slice(0, 16);
  }, [template, categorySlug, getTemplatesByCategory, templates]);

  const scheduleSummary = template?.defaultSchedule
    ? summarizeSchedule(template.defaultSchedule)
    : '';
  const scheduleCatalog = template ? scheduleLabel(template) : '';
  const usageCount = template?.stats?.usageCount || 0;

  const catalogHeroMetaItems = useMemo(() => {
    const rows = [];
    if (scheduleCatalog) rows.push({ icon: 'clock', label: scheduleCatalog });
    if (usageCount > 0) {
      rows.push({
        icon: 'users',
        label: `${usageCount.toLocaleString()} ${t('tasks.uses')}`,
      });
    }
    return rows;
  }, [scheduleCatalog, usageCount, t]);
  const isNew = template ? isNewTemplate(template) : false;

  const openSetup = () => {
    if (!template) return;
    requireAuth(
      () => openModal('SetupTaskFromTemplateModal', { template }),
      {
        source: 'tasks_template_detail_setup',
        title: `Sign in to set up ${template?.name || 'this task'}`,
        subtitle: 'Scheduled tasks run on your account so we know when to start them and where to send the output.',
        icon: 'calendar-clock',
      }
    );
  };

  const canonicalPath = `/tasks/template/${slug}`;
  const metaTitle = template
    ? `${template.name} · ${t('tasks.taskTemplatesTitle')}`
    : t('tasks.taskTemplate');
  const metaDescription =
    template?.description || t('tasks.templateMetaDescriptionFallback');

  const loadingHydration =
    slug &&
    staticTemplate &&
    !liveTemplate &&
    (!isFetched || isLoading);

  if (!slug) {
    return (
      <View style={styles.container}>
        <View style={styles.notFound}>
          <Text type="h2">{t('tasks.missingTemplate')}</Text>
        </View>
      </View>
    );
  }

  if (!template && !loadingHydration) {
    return (
      <View style={styles.container}>
        <MetaHead title={metaTitle} description={metaDescription} path={canonicalPath} />
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.inner}>
            <View style={styles.notFound}>
              <Text type="h2">{t('tasks.templateNotFound')}</Text>
              <Text type="p" style={{ textAlign: 'center', opacity: 0.75 }}>
                {t('tasks.templateRemovedUnavailable')}
              </Text>
              <Button title={t('tasks.browseTemplates')} onPress={() => router.push('/tasks')} />
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (!template && loadingHydration) {
    return (
      <View style={[styles.container, styles.loadingBox]}>
        <MetaHead title={metaTitle} description={metaDescription} path={canonicalPath} />
        <ActivityIndicator size="large" color={accent} />
        <Text type="p" style={{ opacity: 0.7 }}>
          {t('tasks.loadingTemplate')}
        </Text>
      </View>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: template.name,
    description: metaDescription,
    url: `https://gab.ai${canonicalPath}`,
  };

  const fields = Array.isArray(template.configurableFields)
    ? template.configurableFields
    : [];

  return (
    <View style={styles.container}>
      <MetaHead
        title={metaTitle}
        description={metaDescription}
        path={canonicalPath}
        jsonLd={jsonLd}
      />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.fullBleedBand}>
          <CatalogDetailHero
            backLink={{
              label: t('tasks.allTaskTemplates'),
              onPress: () => router.push('/tasks'),
            }}
            categorySlug={categorySlug}
            catalogCategory={{
              label: categoryLabel,
              icon: 'folder',
              ...(categorySlug && String(categorySlug).trim()
                ? { href: `/tasks/category/${encodeURIComponent(String(categorySlug).trim())}` }
                : {}),
            }}
            eyebrowBadge={isNew ? 'new' : null}
            headline={template.name}
            subtitle={template.description || null}
            heroMaxWidth={1100}
            heroIcon="calendar-check"
            primaryAction={{
              label: t('tasks.setUpThisTask'),
              icon: 'calendar-check',
              onPress: openSetup,
              accessibilityLabel: t('tasks.setUpThisTask'),
            }}
            favoriteTemplate={template}
            metaItems={catalogHeroMetaItems}
          />
        </View>

        <View style={styles.inner}>
          {/* "What's a task template?" + "How it runs" — split 50/50 on
              desktop, stacked on small screens. Both cards stretch to
              equal height because the parent uses `alignItems: 'stretch'`
              and each child sets `flex: 1`. */}
          <View style={styles.featureRow}>
            <View style={styles.explainerCard}>
              <View style={styles.explainerHeaderRow}>
                <View style={styles.explainerIconWrap}>
                  <Icon name="sparkles" size={22} color={accent} solid />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={[styles.explainerEyebrow, { color: accent }]}>
                    Why task templates
                  </Text>
                  <Text type="h3" style={{ marginBottom: 0 }}>
                    {t('tasks.whatsTaskTemplateTitle')}
                  </Text>
                </View>
              </View>
              <Text type="p" style={{ lineHeight: 22, opacity: 0.88 }}>
                {t('tasks.whatsTaskTemplateBody')}
              </Text>

              <View style={styles.explainerFeatures}>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="calendar-check" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Pre-wired schedule</Text>
                    {' — pick a cadence once and the Super Intelligence runs it automatically on time.'}
                  </Text>
                </View>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="sliders" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Tweak in seconds</Text>
                    {' — every template exposes the inputs that matter and hides the rest.'}
                  </Text>
                </View>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="inbox" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Results in your inbox</Text>
                    {' — every run shows up in Tasks so you can review, edit, or rerun anytime.'}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.previewFrame}>
              <LinearGradient
                colors={[accent, `${accent}99`]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.previewAccentBar}
              />
              <View style={styles.previewInner}>
                <View style={styles.previewHeaderRow}>
                  <View style={styles.previewIconWrap}>
                    <Icon name="clock" size={16} color={accent} solid />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={[styles.explainerEyebrow, { color: accent }]}>
                      {t('tasks.howItRuns')}
                    </Text>
                    <Text type="h3" style={{ marginBottom: 0 }}>
                      How it runs
                    </Text>
                  </View>
                </View>

                {scheduleSummary ? (
                  <View style={styles.scheduleBox}>
                    <View style={styles.scheduleIconWrap}>
                      <Icon name="arrows-rotate" size={12} color={accent} solid />
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={[styles.sectionLabel, { color: accent, marginBottom: 2 }]}>
                        {t('tasks.defaultSchedule')}
                      </Text>
                      <Text size="s" weight="bold" color="primary">
                        {scheduleSummary}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <View style={styles.scheduleBox}>
                    <View style={styles.scheduleIconWrap}>
                      <Icon name="arrows-rotate" size={12} color={accent} solid />
                    </View>
                    <Text size="s" color="secondary" style={{ flex: 1 }}>
                      {t('tasks.scheduleDetailsWhenSetup')}
                    </Text>
                  </View>
                )}

                {fields.length ? (
                  <>
                    <Text
                      style={[
                        styles.sectionLabel,
                        { color: colors.text_color_secondary, marginTop: 4 },
                      ]}
                    >
                      {t('tasks.whatYouConfigure')}
                    </Text>
                    <View style={styles.stepsWrap}>
                      {fields.map((field, idx) => {
                        const ftype = field?.type || 'text';
                        const iconName = FIELD_TYPE_ICON[ftype] || 'sliders';
                        return (
                          <View key={field.key || idx} style={styles.stepCard}>
                            <View style={styles.stepIcon}>
                              <Icon name={iconName} size={13} color={accent} solid />
                            </View>
                            <View style={{ flex: 1, minWidth: 0, gap: 1 }}>
                              <Text size="s" weight="bold" color="primary" numberOfLines={1}>
                                {field.label || field.key}
                              </Text>
                              <Text style={styles.stepMeta}>
                                {FIELD_TYPE_LABEL[ftype] || ftype}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </>
                ) : null}
              </View>
            </View>
          </View>

          {pageCopyBlocks.length ? (
            <View style={styles.copySection}>
              <ToolPageCopy
                blocks={pageCopyBlocks}
                palette={pageCopyPalette}
                isMD={isSM || isMD}
              />
            </View>
          ) : (
            <View style={styles.explainerCard}>
              <Text type="h3" style={{ marginBottom: 8 }}>
                {t('tasks.aboutThisTemplate')}
              </Text>
              <Text type="p" style={{ lineHeight: 22, opacity: 0.88 }}>
                {template.description || t('tasks.aboutThisTemplateFallback')}
              </Text>
            </View>
          )}

          {/* "Ready to automate?" — centered CTA on a soft accent wash.
              Gradient + blob chroma values match `CatalogDetailHero` so
              this card reads as the same color family as the page hero. */}
          <View style={styles.bottomCta}>
            <LinearGradient
              colors={[colors.solid_color_tertiary, 'transparent']}
              locations={[0, 1]}
              start={{ x: 0.5, y: 1 }}
              end={{ x: 0.5, y: 0.2 }}
              style={styles.bottomCtaWashSoft}
              pointerEvents="none"
            />
            <LinearGradient
              colors={[heroChroma.gradSoft, heroChroma.gradMid, 'transparent']}
              locations={[0, 0.2, 1]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.bottomCtaWashSoft}
              pointerEvents="none"
            />
            <View style={styles.bottomCtaBlobA} pointerEvents="none" />
            <View style={styles.bottomCtaBlobB} pointerEvents="none" />
            <View style={styles.bottomCtaInner}>
              <View style={styles.bottomCtaBadge}>
                <Icon name="calendar-check" size={11} color={accent} solid />
                <Text
                  size="s"
                  weight="extraBold"
                  style={{ color: accent, letterSpacing: 0.6 }}
                >
                  {String(categoryLabel || 'Task template').toUpperCase()}
                </Text>
              </View>
              <Text tag="h2" color="primary" style={styles.bottomCtaTitle}>
                {t('tasks.readyToAutomate')}
              </Text>
              <Text color="secondary" style={styles.bottomCtaBody}>
                {t('tasks.oneTapAddsDescription')}
              </Text>
              <Pressable
                onPress={openSetup}
                accessibilityRole="button"
                accessibilityLabel={t('tasks.setUpThisTask')}
                style={({ hovered, pressed }) => [
                  styles.bottomCtaButton,
                  // Avoid `transform: translateY` on hover — RN Web can
                  // flicker its hover state on the shifted bounds and
                  // trip React's "Maximum update depth" guard.
                  Platform.OS === 'web' && hovered && {
                    boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                  },
                  pressed && { opacity: 0.92 },
                ]}
              >
                <Icon name="calendar-check" size={16} color="#fff" solid />
                <Text size="m" weight="bold" style={{ color: '#fff' }}>
                  {t('tasks.setUpThisTask')}
                </Text>
                <Icon name="arrow-right" size={12} color="#fff" solid />
              </Pressable>
            </View>
          </View>

          {/* Related templates — measured-width cells (no flexGrow) so a
              partial last row keeps card width consistent with the rest
              of the grid; `alignItems: 'stretch'` makes every card in a
              row match the tallest sibling. */}
          {relatedTemplates.length ? (
            <View style={styles.relatedSection}>
              <View style={styles.relatedHeader}>
                <Text type="h3" style={{ marginBottom: 0 }}>
                  {t('tasks.relatedTemplates')}
                </Text>
                <Text size="s" color="secondary" style={{ opacity: 0.7 }}>
                  {categoryLabel}
                </Text>
              </View>
              <View style={styles.relatedCardGrid} onLayout={onRelatedGridLayout}>
                {relatedTemplates.map((rel) => (
                  <View
                    key={rel.id || rel.slug}
                    style={[
                      styles.relatedCell,
                      { width: pinCols === 1 ? '100%' : pinWidthPx },
                    ]}
                  >
                    <TemplatePinCard
                      template={rel}
                      width="100%"
                      stretchHeight={pinCols > 1}
                      onPress={handleTemplateOpenDetail}
                      onSchedulePress={handleTemplateScheduleFromCatalog}
                    />
                  </View>
                ))}
              </View>
            </View>
          ) : null}
        </View>

        <AppDashboardFooter fullViewportWidth />
      </ScrollView>
    </View>
  );
}
